import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  AlertTriangle,
  Download,
  Loader2,
  RefreshCw,
  ShieldAlert,
  TrendingDown,
  TrendingUp,
  Package,
} from 'lucide-react';
import AdminLayout from '../components/AdminLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Feb 2026 — Step 5: Stock Mismatch Report (READ-ONLY, super-admin only).
 *
 * Lists every product whose scalar `stock_qty` disagrees with the sum
 * of its `warehouse_placements.qty`. No actions — this page only
 * surfaces the drift so ops can decide which record to trust.
 */

export default function AdminStockMismatch() {
  const token = localStorage.getItem('mr_admin_token') || '';
  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [minDiff, setMinDiff] = useState(0);
  const [search, setSearch] = useState('');
  const [direction, setDirection] = useState('all'); // all | overstated | understated

  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/reports/stock-mismatch`, {
        params: { min_diff: minDiff, limit: 2000 },
        headers: authHeaders,
      });
      setReport(data);
      setForbidden(false);
    } catch (err) {
      if (err.response?.status === 403) {
        setForbidden(true);
        setReport(null);
      } else {
        toast.error(err.response?.data?.detail || 'Failed to load report.');
      }
    } finally {
      setLoading(false);
    }
  }, [authHeaders, minDiff]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const filteredRows = useMemo(() => {
    if (!report) return [];
    const q = search.trim().toLowerCase();
    return (report.rows || []).filter((r) => {
      if (direction !== 'all' && r.direction !== direction) return false;
      if (!q) return true;
      return (
        r.sku.toLowerCase().includes(q)
        || r.name.toLowerCase().includes(q)
        || r.product_id.toLowerCase().includes(q)
      );
    });
  }, [report, search, direction]);

  const downloadCsv = useCallback(() => {
    if (!report || !filteredRows.length) {
      toast.message('No rows to export.');
      return;
    }
    const header = [
      'SKU', 'Name', 'Category', 'Size', 'UOM',
      'stock_qty (field)', 'placements_qty (physical)', 'diff', 'direction',
      'allocated_qty', 'placement_rows', 'is_active', 'is_visible', 'product_id',
    ];
    const esc = (v) => {
      const s = String(v ?? '');
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [header.map(esc).join(',')];
    for (const r of filteredRows) {
      lines.push([
        r.sku, r.name, r.category_slug, r.size, r.uom,
        r.stock_qty, r.placements_qty, r.diff, r.direction,
        r.allocated_qty, r.placement_rows, r.is_active, r.is_visible, r.product_id,
      ].map(esc).join(','));
    }
    const blob = new Blob(['\uFEFF' + lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    a.href = url;
    a.download = `stock-mismatch-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filteredRows.length} rows.`);
  }, [report, filteredRows]);

  if (forbidden) {
    return (
      <AdminLayout>
        <div
          className="max-w-xl mx-auto mt-24 bg-rose-50 border border-rose-200 rounded-xl p-8 text-center"
          data-testid="stock-mismatch-forbidden"
        >
          <ShieldAlert className="w-10 h-10 text-rose-600 mx-auto mb-3" />
          <div className="text-lg font-semibold text-rose-900">Super admin only</div>
          <div className="text-sm text-rose-700 mt-2">
            This diagnostic report is restricted to super administrators.
          </div>
        </div>
      </AdminLayout>
    );
  }

  const summary = report?.summary || {
    mismatched_products: 0,
    overstated: 0,
    understated: 0,
    total_abs_diff_units: 0,
  };

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto space-y-6" data-testid="stock-mismatch-page">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-6 h-6 text-amber-600" />
              <h1 className="text-2xl font-semibold text-neutral-900">
                Stock Mismatch Report
              </h1>
              <span className="px-2 py-0.5 text-[11px] font-semibold rounded-full bg-amber-100 text-amber-800">
                Read-only
              </span>
            </div>
            <div className="text-sm text-neutral-600 mt-1 max-w-2xl">
              Lists every product where <code className="font-mono">stock_qty</code>{' '}
              (the denormalised scalar on <code className="font-mono">products</code>)
              disagrees with the sum of <code className="font-mono">warehouse_placements.qty</code>{' '}
              (physical rack state). No writes — this report only surfaces drift.
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={loadReport}
              disabled={loading}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-neutral-300 bg-white text-sm font-semibold text-neutral-700 hover:bg-neutral-100 disabled:opacity-50"
              data-testid="stock-mismatch-refresh"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Refresh
            </button>
            <button
              type="button"
              onClick={downloadCsv}
              disabled={loading || !filteredRows.length}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50"
              data-testid="stock-mismatch-export-csv"
            >
              <Download className="w-4 h-4" /> Export CSV
            </button>
          </div>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <Card
            label="Mismatched products"
            value={summary.mismatched_products}
            icon={<Package className="w-5 h-5 text-neutral-500" />}
            testid="stock-mismatch-summary-total"
          />
          <Card
            label="stock_qty too high"
            value={summary.overstated}
            icon={<TrendingUp className="w-5 h-5 text-rose-500" />}
            tone="rose"
            testid="stock-mismatch-summary-over"
          />
          <Card
            label="stock_qty too low"
            value={summary.understated}
            icon={<TrendingDown className="w-5 h-5 text-sky-500" />}
            tone="sky"
            testid="stock-mismatch-summary-under"
          />
          <Card
            label="Total drift (abs units)"
            value={summary.total_abs_diff_units}
            icon={<AlertTriangle className="w-5 h-5 text-amber-500" />}
            tone="amber"
            testid="stock-mismatch-summary-drift"
          />
        </div>

        {/* Filters */}
        <div className="rounded-lg border border-neutral-200 bg-white px-4 py-3 flex flex-wrap items-end gap-4">
          <label className="flex flex-col text-xs font-semibold text-neutral-600">
            Search (SKU, name, product id)
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Type to filter…"
              className="mt-1 px-3 py-1.5 rounded border border-neutral-300 text-sm font-normal w-72"
              data-testid="stock-mismatch-search"
            />
          </label>
          <label className="flex flex-col text-xs font-semibold text-neutral-600">
            Direction
            <select
              value={direction}
              onChange={(e) => setDirection(e.target.value)}
              className="mt-1 px-2 py-1.5 rounded border border-neutral-300 text-sm font-normal"
              data-testid="stock-mismatch-direction"
            >
              <option value="all">All drifts</option>
              <option value="overstated">stock_qty too high</option>
              <option value="understated">stock_qty too low</option>
            </select>
          </label>
          <label className="flex flex-col text-xs font-semibold text-neutral-600">
            Hide drift ≤
            <input
              type="number"
              value={minDiff}
              min={0}
              onChange={(e) => setMinDiff(Math.max(0, Number(e.target.value) || 0))}
              className="mt-1 px-3 py-1.5 rounded border border-neutral-300 text-sm font-normal w-28"
              data-testid="stock-mismatch-min-diff"
            />
          </label>
          <div className="text-xs text-neutral-500 ml-auto">
            Showing{' '}
            <span className="font-semibold text-neutral-700">{filteredRows.length}</span>{' '}
            of {report?.rows?.length || 0}
            {report?.truncated ? (
              <span className="ml-1 text-amber-600 font-semibold">(truncated — refine filters)</span>
            ) : null}
          </div>
        </div>

        {/* Table */}
        <div className="rounded-lg border border-neutral-200 bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm" data-testid="stock-mismatch-table">
              <thead className="bg-neutral-50 text-[11px] uppercase tracking-wider text-neutral-500 border-b border-neutral-200">
                <tr>
                  <th className="py-2 px-3 text-left font-semibold">SKU</th>
                  <th className="py-2 px-3 text-left font-semibold">Product</th>
                  <th className="py-2 px-3 text-left font-semibold">Category</th>
                  <th className="py-2 px-3 text-right font-semibold">stock_qty</th>
                  <th className="py-2 px-3 text-right font-semibold">Placements</th>
                  <th className="py-2 px-3 text-right font-semibold">Diff</th>
                  <th className="py-2 px-3 text-left font-semibold">Direction</th>
                  <th className="py-2 px-3 text-right font-semibold">Allocated</th>
                  <th className="py-2 px-3 text-right font-semibold">Rows</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {loading && (
                  <tr>
                    <td colSpan={9} className="py-8 text-center text-neutral-500">
                      <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" />
                      Scanning placements vs stock_qty…
                    </td>
                  </tr>
                )}
                {!loading && filteredRows.length === 0 && (
                  <tr>
                    <td colSpan={9} className="py-8 text-center text-neutral-500" data-testid="stock-mismatch-empty">
                      No drifts match the current filters.
                    </td>
                  </tr>
                )}
                {!loading && filteredRows.map((r) => (
                  <tr
                    key={r.product_id}
                    data-testid={`stock-mismatch-row-${r.product_id}`}
                    className="hover:bg-neutral-50"
                  >
                    <td className="py-1.5 px-3 font-mono text-[#0078D4] font-semibold">{r.sku || '—'}</td>
                    <td className="py-1.5 px-3 text-neutral-800">
                      <div className="font-medium">{r.name || '—'}</div>
                      {r.size && (
                        <div className="text-[10px] text-neutral-500 mt-0.5">{r.size} · {r.uom}</div>
                      )}
                    </td>
                    <td className="py-1.5 px-3 text-neutral-600">{r.category_slug || '—'}</td>
                    <td className="py-1.5 px-3 text-right tabular-nums text-neutral-900 font-semibold">
                      {fmtInt(r.stock_qty)}
                    </td>
                    <td className="py-1.5 px-3 text-right tabular-nums text-neutral-900 font-semibold">
                      {fmtInt(r.placements_qty)}
                    </td>
                    <td className={`py-1.5 px-3 text-right tabular-nums font-bold ${r.direction === 'overstated' ? 'text-rose-600' : 'text-sky-600'}`}>
                      {r.diff > 0 ? '+' : ''}{fmtInt(r.diff)}
                    </td>
                    <td className="py-1.5 px-3">
                      <DirectionPill direction={r.direction} />
                    </td>
                    <td className="py-1.5 px-3 text-right tabular-nums text-neutral-600">
                      {fmtInt(r.allocated_qty)}
                    </td>
                    <td className="py-1.5 px-3 text-right tabular-nums text-neutral-500">
                      {fmtInt(r.placement_rows)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {report?.generated_at && (
          <div className="text-[11px] text-neutral-400 text-right">
            Generated {fmtTs(report.generated_at)} · Read-only diagnostic
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

// ---------------- Helpers ----------------

function Card({ label, value, icon, tone = 'neutral', testid }) {
  const toneBg = {
    neutral: 'bg-white border-neutral-200',
    rose: 'bg-rose-50 border-rose-200',
    sky: 'bg-sky-50 border-sky-200',
    amber: 'bg-amber-50 border-amber-200',
  }[tone] || 'bg-white border-neutral-200';
  return (
    <div
      className={`rounded-lg border px-4 py-3 ${toneBg}`}
      data-testid={testid}
    >
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider font-semibold text-neutral-500">
        {icon}
        {label}
      </div>
      <div className="mt-1 text-2xl font-bold tabular-nums text-neutral-900">
        {fmtInt(value)}
      </div>
    </div>
  );
}

function DirectionPill({ direction }) {
  if (direction === 'overstated') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full bg-rose-100 text-rose-800 border border-rose-200">
        <TrendingUp className="w-3 h-3" /> too high
      </span>
    );
  }
  if (direction === 'understated') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full bg-sky-100 text-sky-800 border border-sky-200">
        <TrendingDown className="w-3 h-3" /> too low
      </span>
    );
  }
  return <span className="text-neutral-400">—</span>;
}

function fmtInt(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—';
  return Number(n).toLocaleString();
}

function fmtTs(ts) {
  if (!ts) return '';
  try {
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return String(ts);
    return d.toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch (_) {
    return String(ts);
  }
}
